import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  Button,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Container,
  Select,
  MenuItem,
  FormControl,
  Switch,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";

import {
  useGetCinemaByIdQuery,
  useUpdateTheatreMutation,
} from "../../../services/cinema.query";
import { useGetMallsQuery } from "../../../services/mall.query";
import { Notify } from "../../../layouts/notify";
import { DefaultPage } from "../../../layouts/default-page";

const DataRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box mb={1}>
    <Typography
      variant="body2"
      color="text.secondary"
      fontWeight="bold"
      component="span"
      sx={{ fontSize: "0.95rem" }}
    >
      {label}:{" "}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "#0020ed", fontWeight: 500, fontSize: "1rem" }}
      component="span"
    >
      {value ?? "N/A"}
    </Typography>
  </Box>
);

export default function TheatreDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const isValidId = !!id && /^\d+$/.test(id);

  const { data: theatre, isLoading, refetch } = useGetCinemaByIdQuery(id!, {
    skip: !isValidId,
  });
  const { data: malls = [] } = useGetMallsQuery("");
  const [updateTheatre, { isLoading: isUpdating }] = useUpdateTheatreMutation();

  const currentYear = new Date().getFullYear();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      mall_id: theatre?.mall_id ?? "",
      type: theatre?.type ?? "REGULAR",
      is_active: theatre?.is_active ?? true,
    },
    onSubmit: async (values) => {
      if (!theatre?.id) return;

      try {
        await updateTheatre({
          id: theatre.id,
          mall_id: values.mall_id === "" ? null : Number(values.mall_id),
          type: values.type,
          is_active: values.is_active,
        }).unwrap();

        Notify("info", "Theatre updated successfully");
        refetch();
      } catch {
        Notify("error", "Failed to update theatre");
      }
    },
  });

  if (isLoading || !theatre) {
    return (
      <Box p={3}>
        <Skeleton variant="rectangular" width={120} height={36} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={50} sx={{ mb: 2 }} />
      </Box>
    );
  }

  const group = theatre.theatre_group;
  
  if (!isValidId) {
    return (
      <DefaultPage title="Theatre Details" loading={false}>
        <Typography color="error">Invalid theatre ID.</Typography>
      </DefaultPage>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          size="medium"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate("/movie-house/theaters")}
        >
          Back
        </Button>

        <Button
          size="medium"
          variant="contained"
          startIcon={<Save />}
          onClick={() => formik.handleSubmit()}
          disabled={isUpdating}
        >
          {isUpdating ? "Updating..." : "Update"}
        </Button>
      </Stack>

      <Typography variant="h5" fontWeight="bold" mb={2}>
        {theatre.name}
      </Typography>

      <Card sx={{ mb: 3, boxShadow: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Theatre
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DataRow label="MTRCB Reg. ID" value={theatre.mtrcb_reg_id} />
              <DataRow
                label="Last Reg. Year"
                value={
                  <Typography color={theatre.last_reg_year === currentYear ? "inherit" : "red"}>
                    {theatre.last_reg_year}
                  </Typography>
                }
              />
              <DataRow label="Street" value={theatre.street_address} />
              <DataRow label="Barangay" value={theatre.barangay_address} />
              <DataRow label="City" value={theatre.city_address} />
              <DataRow label="Province" value={theatre.province_address} />
              <DataRow label="Region" value={theatre.region_address} />
              <DataRow label="ZIP Code" value={theatre.zip_code_address} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Typography fontSize="0.85rem" mb={0.5}>
                  Mall
                </Typography>
                <Select
                  name="mall_id"
                  value={formik.values.mall_id}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">No Mall Linked</MenuItem>
                  {malls.map((mall: any) => (
                    <MenuItem key={mall.id} value={mall.id}>
                      {mall.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Typography fontSize="0.85rem" mb={0.5}>
                  Cinema Type
                </Typography>
                <Select
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="REGULAR">Regular</MenuItem>
                  <MenuItem value="SPECIAL">Special</MenuItem>
                </Select>
              </FormControl>

              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography fontSize="0.95rem">Active Status</Typography>
                <Switch
                  checked={formik.values.is_active}
                  onChange={(e) => formik.setFieldValue("is_active", e.target.checked)}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {group && (
        <Card sx={{ boxShadow: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Linked Mall
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Avatar
                src={group.logo_url}
                alt={group.name}
                variant="rounded"
                sx={{ width: 60, height: 60 }}
              />
              <Typography fontWeight="bold" fontSize="1.2rem">
                {group.name}
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DataRow label="Code" value={group.code_pass} />
                <DataRow label="Street" value={group.street_address} />
                <DataRow label="Barangay" value={group.barangay_address} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DataRow label="City" value={group.city_address} />
                <DataRow label="Province" value={group.province_address} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}